import { useMemo } from "react";
import { AdvancedMarker, Circle, Map as GoogleMap, Pin } from "@vis.gl/react-google-maps";

import { FitBounds } from "@/components/maps/FitBounds";
import { GoogleMapsGate } from "@/components/maps/GoogleMapsGate";
import { GOOGLE_MAPS_MAP_ID, primaryColor } from "@/lib/google-maps";
import { padForRadius, type Point } from "@/lib/map-geometry";

/**
 * The employee's home and the circle a check-in must fall inside to count as
 * working from home, on the profile page (BUG-2026-015) — so a saved location
 * can be recognised at a glance rather than from its coordinates.
 *
 * Decoration only: the server decides what counts as home (BR-062). With no
 * radius known — an edit clearing the employee's own, while the organisation
 * default is not reported — only the pin is drawn, rather than a wrong circle.
 */
export function HomeMap({
  latitude,
  longitude,
  radiusM,
}: {
  latitude: number;
  longitude: number;
  radiusM: number | null;
}) {
  const color = useMemo(() => primaryColor(), []);
  const home = useMemo<Point>(() => ({ lat: latitude, lng: longitude }), [latitude, longitude]);
  const fit = useMemo(() => (radiusM ? padForRadius(home, radiusM) : [home]), [home, radiusM]);

  return (
    <GoogleMapsGate className="h-44 w-full">
      <div className="h-44 w-full overflow-hidden rounded-lg border">
        <GoogleMap
          mapId={GOOGLE_MAPS_MAP_ID}
          defaultCenter={home}
          defaultZoom={16}
          gestureHandling="cooperative"
          disableDefaultUI
          zoomControl
          clickableIcons={false}
          style={{ width: "100%", height: "100%" }}
        >
          <FitBounds points={fit} />
          {radiusM != null && (
            <Circle
              center={home}
              radius={radiusM}
              clickable={false}
              strokeColor={color}
              strokeWeight={2}
              strokeOpacity={0.9}
              fillColor={color}
              fillOpacity={0.12}
            />
          )}
          <AdvancedMarker position={home} title="Home">
            <Pin background={color} borderColor="#ffffff" glyphColor="#ffffff" />
          </AdvancedMarker>
        </GoogleMap>
      </div>
    </GoogleMapsGate>
  );
}
